<template>
  <div class="donation-container">
    <div class="donation-card">
      <div class="header">
        <h1 class="title">{{ $t("donate") }}</h1>
        <p class="subtitle">Support this organization with your contribution</p>
      </div>

      <div v-if="loading" class="loading-state">
        <v-progress-circular indeterminate color="#4a6fa5" size="64" />
      </div>

      <div v-else class="content">
        <!-- Organization -->
        <div class="organization-card" v-if="agency" @click="goToProfile">
          <div class="org-avatar">
            <agency-avatar
              :img="agency.midThumbnail ? agency.midThumbnail : agency.image"
              :name="agency.name"
              :size="56"
            />
          </div>
          <div class="org-info">
            <h3 class="org-name">{{ agency.name }}</h3>
          </div>
          <v-icon class="chevron">mdi-chevron-right</v-icon>
        </div>

        <!-- Amount -->
        <div class="amount-section">
          <h3 class="section-title">Select donation amount</h3>
          <div class="amount-grid">
            <div
              v-for="(option, index) in amountOptions"
              :key="index"
              class="amount-option"
              :class="{
                'selected': amount == option.value,
                'popular': option.popular
              }"
              @click="selectAmount(option.value)"
            >
              <span class="amount">${{ option.value }}</span>
              <span v-if="option.popular" class="popular-badge">Popular</span>
              <div class="selection-indicator">
                <v-icon color="#4a6fa5">mdi mdi-heart-circle</v-icon>
              </div>
            </div>
          </div>

          <div class="custom-amount">
            <div class="input-container">
              <span class="currency-symbol">$</span>
              <input
                type="number"
                v-model="amount"
                placeholder="Other amount"
                @focus="handleCustomAmountFocus"
                @blur="onRoundAmount"
              />
            </div>
          </div>
        </div>

        <!-- Personal Message -->
        <div class="message-section">
          <h3 class="section-title">Add a personal message (optional)</h3>
          <div class="message-input">
            <textarea
              v-model="description"
              placeholder="Your message will be shared with the organization"
              rows="3"
              maxlength="200"
            ></textarea>
            <div class="char-count">{{ description.length }}/200</div>
          </div>
        </div>

        <!-- Actions -->
        <div class="actions">
          <button
            class="donate-button"
            :class="{ 'disabled': !amount || amount <= 0 }"
            @click="goToPayment"
          >
            <span>Donate ${{ amount || '0' }}</span>
            <v-icon right>mdi-arrow-right</v-icon>
          </button>
          <button class="cancel-button" @click="cancelPayment">
            Not now
          </button>
        </div>
      </div>
    </div>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :timeout="3000" top>
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import AgencyAvatar from "@/components/agency/AgencyAvatar.vue";
import DOMPurify from "dompurify";

export default {
  components: { AgencyAvatar },
  data() {
    return {
      loading: false,
      errorMessage: null,
      snackbar: false,
      agency: null,
      amount: null,
      description: "",
      isCustomAmountFocused: false,
      amountOptions: [
        { value: 10 },
        { value: 25, popular: true },
        { value: 50 },
        { value: 100 },
        { value: 250 },
        { value: 500 }
      ]
    };
  },
  methods: {
    ...mapActions({
      getAgency: "agency/getAgency"
    }),
    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbar = true;
    },
    goToProfile() {
      let routeData = this.$router.resolve({
        name: "agency-profile-details",
        params: { id: this.$route.query.id }
      });
      window.open(routeData.href, "_blank");
    },
    onRoundAmount() {
      try {
        this.isCustomAmountFocused = false;
        if (!this.amount) return;
        this.amount = parseFloat(`${this.amount}`).toFixed(2);
      } catch (error) {
        console.log(error);
      }
    },
    handleCustomAmountFocus() {
      this.isCustomAmountFocused = true;
      this.amount = null;
    },
    selectAmount(value) {
      this.amount = value.toFixed(2);
      this.isCustomAmountFocused = false;
    },
goToPayment() {
  if (this.amount && this.$route.query.id) {
    // Sanitize description before sending
    const safeDescription = DOMPurify.sanitize(this.description, {
      ALLOWED_TAGS: [], // no HTML allowed
      ALLOWED_ATTR: []
    });

    this.$router.replace({
      name: "donation-payment",
      query: {
        id: this.$route.query.id,
        amount: this.amount,
        description: safeDescription
      }
    });
  }
},
    cancelPayment() {
      this.$router.push("/donations");
    }
  },
  mounted() {
    this.loading = true;
    this.getAgency(this.$route.query.id)
      .then(res => {
        this.loading = false;
        this.agency = res;
      })
      .catch(error => {
        this.loading = false;
        if (error.response == undefined) {
          console.log(error);
        } else {
          this.showErrorMessage(error.response.data.message);
        }
        this.$router.replace({ name: "donation-select-agency" });
      });
  }
};
</script>


<style scoped>
.donation-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 24px;
}

.donation-card {
  width: 100%;
  min-width: 870px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.header {
  padding: 32px 32px 24px;
  background: linear-gradient(135deg, #084468 0%, #1e658d 100%);
  color: white;
}

.title {
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 8px;
  font-family: 'Poppins', sans-serif;
}

.subtitle {
  font-size: 16px;
  opacity: 0.9;
  font-weight: 400;
  font-family: 'Poppins', sans-serif;
}

.loading-state {
  padding: 64px 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.content {
  padding: 24px 32px 32px;
}

.organization-card {
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  background-color: #f8f9fa;
  margin-bottom: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
}

.organization-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.org-avatar {
  margin-right: 16px;
}

.org-info {
  flex: 1;
}

.org-name {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #2d3748;
  font-family: 'Poppins', sans-serif;
}

.org-rating {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #718096;
}

.org-rating i {
  margin-right: 4px;
}

.chevron {
  color: #a0aec0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #2d3748;
  font-family: 'Poppins', sans-serif;
}

.amount-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.amount-option {
  position: relative;
  height: 72px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background-color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
}

.amount-option:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
}

.amount {
  font-size: 18px;
  font-weight: 600;
  color: #4a5568;
  transition: all 0.3s ease;
}

.popular-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: #48bb78;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 500;
}

.selection-indicator {
  position: absolute;
    top: 0px;
    right: 0px;
  width: 24px;
  height: 24px;
  /* background-color: #4a6fa5; */
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.3s ease;
}

.amount-option.selected {
  border-color: #4a6fa5;
  background-color: rgba(74, 111, 165, 0.05);
}

.amount-option.selected .amount {
  color: #4a6fa5;
  font-weight: 700;
}

.amount-option.selected .selection-indicator {
  opacity: 1;
  transform: scale(1);
}

.amount-option.popular {
  border: 1px solid #48bb78;
}

.custom-amount {
  margin-bottom: 24px;
}

.input-container {
  position: relative;
}

.currency-symbol {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: #4a5568;
  font-weight: 500;
}

.input-container input {
  width: 100%;
  padding: 16px 16px 16px 40px;
  font-size: 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-family: 'Poppins', sans-serif;
  transition: all 0.3s ease;
}

.input-container input:focus {
  outline: none;
  border-color: #4a6fa5;
}

.underline {
  position: absolute;
  bottom: 0;
  left: 10px;
  right: 0;
  height: 2px;
  background-color: #4a6fa5;
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.underline.active {
  transform: scaleX(1);
}

.message-section {
  margin-bottom: 32px;
}

.message-input {
  position: relative;
}

.message-input textarea {
  width: 100%;
  padding: 16px;
  font-size: 14px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-family: 'Poppins', sans-serif;
  transition: all 0.3s ease;
  resize: none;
}

.message-input textarea:focus {
  outline: none;
  border-color: #4a6fa5;
}

.char-count {
  position: absolute;
  bottom: 8px;
  right: 8px;
  font-size: 12px;
  color: #a0aec0;
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.donate-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border-radius: 8px;
  background: linear-gradient(135deg, #084468 0%, #1e658d 100%);
  color: white;
  font-size: 16px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Poppins', sans-serif;
  box-shadow: 0 4px 6px rgba(74, 111, 165, 0.2);
}

.donate-button:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(74, 111, 165, 0.3);
}

.donate-button:active:not(.disabled) {
  transform: translateY(0);
}

.donate-button.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #e2e8f0;
  color: #a0aec0;
}

.cancel-button {
  padding: 16px;
  border-radius: 8px;
  background: transparent;
  color: #4a5568;
  font-size: 16px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Poppins', sans-serif;
}

.cancel-button:hover {
  background-color: #f8f9fa;
}

@media (max-width: 600px) {
  .donation-container {
    padding: 16px;
  }
  
  .donation-card {
    border-radius: 12px;
  }
  
  .header {
    padding: 24px 24px 16px;
  }
  
  .content {
    padding: 20px 24px 24px;
  }
  
  .amount-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>